import { useEffect, useState } from "react";
import { ScrollText, Users, ShieldAlert, BookOpen, FileSignature, Download, Loader2, Scale } from "lucide-react";
import { AdminLayout } from "@/components/AdminLayout";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { DocumentGenerateDialog } from "@/components/DocumentGenerateDialog";
import { ImplementationGuideDialog } from "@/components/ImplementationGuideDialog";
import { SignableDocumentRow } from "@/components/SignableDocumentRow";
import { SignatureStatusPanel } from "@/components/SignatureStatusPanel";
import { VersionHistoryDialog } from "@/components/VersionHistoryDialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { POLICY_LIBRARY, DOCUMENT_LIBRARY } from "@/constants/testIds";
import { toast } from "sonner";

function TemplateCard({ template, employees, onGenerated }) {
  return (
    <div data-testid={DOCUMENT_LIBRARY.templateCard} className="bg-card border border-petroleum/10 rounded-2xl p-4 flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium text-charcoal text-sm">{template.name}</p>
        {template.group === "acuse" ? (
          <span className="shrink-0 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">Acuse de lectura</span>
        ) : (
          <span className="shrink-0 text-xs font-medium text-terracotta bg-terracotta/10 px-2 py-0.5 rounded-full">Firma física</span>
        )}
      </div>
      <p className="text-xs text-charcoal/60 leading-relaxed flex-1">{template.description}</p>
      {template.legal_reason && (
        <p data-testid={DOCUMENT_LIBRARY.legalReason} className="text-xs text-charcoal/45 leading-relaxed flex items-start gap-1 border-t border-petroleum/10 pt-2">
          <Scale className="w-3 h-3 mt-0.5 shrink-0" strokeWidth={1.5} /> <span>{template.legal_reason}</span>
        </p>
      )}
      <div className="flex justify-end items-center gap-1">
        <VersionHistoryDialog docType={template.id} templateName={template.name} employees={employees} needsEmployee={template.scope === "employee"} />
        <DocumentGenerateDialog template={template} employees={employees} onGenerated={onGenerated} />
      </div>
    </div>
  );
}

export default function PolicyLibrary() {
  const [templates, setTemplates] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [distributeTarget, setDistributeTarget] = useState(null);
  const [distributeAll, setDistributeAll] = useState(false);
  const [distributeEmployees, setDistributeEmployees] = useState([]);
  const [pdfDownloadingId, setPdfDownloadingId] = useState(null);
  const [signableEmployeeId, setSignableEmployeeId] = useState("");
  const [signableDocs, setSignableDocs] = useState([]);
  const [loadingSignableDocs, setLoadingSignableDocs] = useState(false);

  const load = () => {
    api.get("/document-templates").then(({ data }) => setTemplates(data));
    api.get("/policies").then(({ data }) => setPolicies(data));
    api.get("/employees").then(({ data }) => setEmployees(data));
  };

  useEffect(load, []);

  const acuseTemplates = templates.filter((t) => t.group === "acuse");
  const firmaTemplates = templates.filter((t) => t.group === "firma");

  const handleDistribute = async () => {
    try {
      const payload = distributeAll ? { all_employees: true } : { employee_ids: distributeEmployees };
      const { data } = await api.post(`/policies/${distributeTarget}/distribute`, payload);
      toast.success(data.message);
      setDistributeTarget(null);
      setDistributeAll(false);
      setDistributeEmployees([]);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleDistributeEmployee = (id) =>
    setDistributeEmployees((prev) => (prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]));

  const handleDownloadPolicyPdf = async (policy) => {
    setPdfDownloadingId(policy.id);
    try {
      const { data } = await api.get(`/policies/${policy.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${policy.title}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el PDF.");
    } finally {
      setPdfDownloadingId(null);
    }
  };

  const loadSignableDocs = (employeeId) => {
    setSignableEmployeeId(employeeId);
    if (!employeeId) {
      setSignableDocs([]);
      return;
    }
    setLoadingSignableDocs(true);
    api
      .get(`/employees/${employeeId}/signable-documents`)
      .then(({ data }) => setSignableDocs(data))
      .finally(() => setLoadingSignableDocs(false));
  };

  const handleDownloadSignablePdf = async (doc) => {
    try {
      const { data } = await api.get(`/signable-documents/${doc.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doc.title}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el PDF.");
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-heading text-3xl font-bold text-charcoal tracking-tight">Biblioteca de Documentos</h1>
            <p className="text-charcoal/60 mt-1">Genera documentos legales auto-llenados con los datos de tu empresa y expedientes — sin texto libre.</p>
          </div>
          <ImplementationGuideDialog />
        </div>

        <div className="bg-card border border-petroleum/10 rounded-2xl p-4 mt-5 flex items-start gap-2">
          <ShieldAlert className="w-4 h-4 text-charcoal/40 mt-0.5 shrink-0" strokeWidth={1.5} />
          <p data-testid={POLICY_LIBRARY.legalDisclaimer} className="text-xs text-charcoal/50">CERH no sustituye la asesoría de un abogado laboral. Todos los documentos generados aquí quedan pendientes de validación por un abogado laboral antes de tener carácter definitivo.</p>
        </div>

        <SignatureStatusPanel />

        <div data-testid={DOCUMENT_LIBRARY.acuseSection} className="mt-8">
          <h2 className="font-heading text-lg font-semibold text-charcoal mb-1 flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-petroleum" strokeWidth={1.5} /> Documentos generales (acuse de lectura)
          </h2>
          <p className="text-xs text-charcoal/50 mb-3">Se generan una vez para toda la empresa y se distribuyen para que cada persona confirme su lectura.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {acuseTemplates.map((t) => (
              <TemplateCard key={t.id} template={t} employees={employees} onGenerated={load} />
            ))}
          </div>
        </div>

        <div data-testid={DOCUMENT_LIBRARY.firmaSection} className="mt-8">
          <h2 className="font-heading text-lg font-semibold text-charcoal mb-1 flex items-center gap-2">
            <FileSignature className="w-4 h-4 text-terracotta" strokeWidth={1.5} /> Documentos por empleado (firma física)
          </h2>
          <p className="text-xs text-charcoal/50 mb-3">
            Se generan para una persona específica. Para estos documentos, CERH usa <strong>firma autógrafa (física)</strong>{" "}
            como respaldo probatorio: se envían al portal para revisión electrónica, se firman en papel entre ambas partes,
            y luego se sube la foto o escaneo del documento firmado como respaldo digital. La ley también admite otros
            medios electrónicos como prueba (Art. 776 y 836-A a 836-D LFT); la firma física es la vía que usa CERH hoy
            por su fuerza práctica ante una inspección o un litigio, no porque sea la única forma legalmente válida.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {firmaTemplates.map((t) => (
              <TemplateCard key={t.id} template={t} employees={employees} onGenerated={load} />
            ))}
          </div>
        </div>

        <h2 className="font-heading text-xl font-semibold text-charcoal mt-10 mb-4">Políticas de la empresa (acuse)</h2>
        <div className="space-y-3">
          {policies.map((p) => (
            <div key={p.id} data-testid={POLICY_LIBRARY.policyCard} className="bg-card border border-petroleum/10 rounded-xl p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-semibold text-charcoal flex items-center gap-2">
                    <ScrollText className="w-4 h-4 text-petroleum" strokeWidth={1.5} /> {p.title}
                  </p>
                  {p.article_ref && <p className="text-xs font-mono-legal text-petroleum/70 mt-1">{p.article_ref}</p>}
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  {p.doc_type && (
                    <VersionHistoryDialog docType={p.doc_type} templateName={p.title} employees={employees} needsEmployee={false} />
                  )}
                  <Button
                    data-testid={`${DOCUMENT_LIBRARY.policyPdfButton}-${p.id}`}
                    size="sm" variant="outline" onClick={() => handleDownloadPolicyPdf(p)} disabled={pdfDownloadingId === p.id}
                    className="rounded-full border-petroleum/30 text-petroleum"
                  >
                    {pdfDownloadingId === p.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} PDF
                  </Button>
                  <Dialog open={distributeTarget === p.id} onOpenChange={(open) => setDistributeTarget(open ? p.id : null)}>
                    <DialogTrigger asChild>
                      <Button data-testid={`${POLICY_LIBRARY.distributeButton}-${p.id}`} size="sm" className="bg-terracotta hover:bg-terracotta/90 rounded-full">
                        <Users className="w-4 h-4 mr-1" /> Distribuir
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-bone">
                      <DialogHeader>
                        <DialogTitle>Distribuir "{p.title}"</DialogTitle>
                        <DialogDescription>Elige a qué empleados asignar este documento para su acuse de lectura.</DialogDescription>
                      </DialogHeader>
                      <label className="flex items-center gap-3 text-sm cursor-pointer">
                        <Checkbox data-testid={POLICY_LIBRARY.distributeAllCheckbox} checked={distributeAll} onCheckedChange={(v) => setDistributeAll(!!v)} />
                        Distribuir a todos los empleados
                      </label>
                      {!distributeAll && (
                        <div className="space-y-2 max-h-56 overflow-y-auto">
                          {employees.map((e) => (
                            <label key={e.id} className="flex items-center gap-3 text-sm cursor-pointer">
                              <Checkbox data-testid={`${POLICY_LIBRARY.distributeEmployeeCheckbox}-${e.id}`} checked={distributeEmployees.includes(e.id)} onCheckedChange={() => toggleDistributeEmployee(e.id)} />
                              {e.full_name}
                            </label>
                          ))}
                        </div>
                      )}
                      <Button data-testid={POLICY_LIBRARY.distributeConfirmButton} onClick={handleDistribute} disabled={!distributeAll && distributeEmployees.length === 0} className="w-full bg-petroleum rounded-full">
                        Confirmar distribución
                      </Button>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
              <p className="text-xs text-charcoal/50 mt-3">{p.assigned_count} asignada(s) · {p.pending_count} pendiente(s) de acuse</p>
            </div>
          ))}
          {policies.length === 0 && <p className="text-charcoal/50 text-sm">Aún no has generado ninguna política.</p>}
        </div>

        <h2 className="font-heading text-xl font-semibold text-charcoal mt-10 mb-1">Documentos por empleado (firma)</h2>
        <p className="text-xs text-charcoal/50 mb-4">Selecciona un empleado para ver los contratos, convenios y anexos generados a su nombre.</p>
        <div className="mb-4 max-w-sm">
          <Select value={signableEmployeeId} onValueChange={loadSignableDocs}>
            <SelectTrigger data-testid={DOCUMENT_LIBRARY.employeeDocsSelect} className="bg-white"><SelectValue placeholder="Selecciona un empleado" /></SelectTrigger>
            <SelectContent>
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {loadingSignableDocs && <Loader2 className="w-5 h-5 text-petroleum animate-spin" />}
        {!loadingSignableDocs && signableEmployeeId && (
          <div className="space-y-2">
            {signableDocs.map((d) => (
              <SignableDocumentRow key={d.id} doc={d} onDownloadPdf={handleDownloadSignablePdf} onChanged={() => loadSignableDocs(signableEmployeeId)} />
            ))}
            {signableDocs.length === 0 && <p className="text-charcoal/50 text-sm">Sin documentos de firma generados para esta persona.</p>}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
